import Chart from 'chart.js/auto'

import CustomHTMLElement from '@onpace/onspace-core/components/html_element'

export default class OnspaceChart extends CustomHTMLElement {
  /// Sets up the element and chart.
  ///
  /// The following parameters are accepted as either options or data attributes:
  /// [chartType or data-type]
  ///   The type of chart to display.
  /// [chartData or data-data]
  ///   The data to display in the chart.
  /// [chartSummary or data-summary]
  ///   Removes most of the UI from around the chart.
  /// [hideLegend or data-hide-legend]
  ///   Removes the legend from the UI.
  /// [formatKey]
  ///   A callback which is run to parse and display a raw data key.
  runFirstConnected(options = {}) {
    super.runFirstConnected()

    this.chartType = this.getAttribute('data-type')
    if (!this.chartType && !!options.type) {
      this.chartType = options.type
      this.setAttribute('data-type', this.chartType)
    }

    this.chartSummary = options.summary || this.getBooleanAttribute('data-summary')
    this.hideLegend = options.hideLegend || this.getBooleanAttribute('data-hide-legend')

    this.formatKeyCallback = options.formatKey

    this.chartData = options.data || this.getJsonAttribute('data-data') || []

    this.setupChart()
  }

  /// Run when the element is connected to the DOM.
  ///
  /// This sets up listeners to automatically update the chart's styles as necessary.
  runConnected() {
    super.runConnected()

    if (window.matchMedia) {
      this.colorSchemeMatcher = window.matchMedia('(prefers-color-scheme: dark)')
      this.colorSchemeListener = this.updateChartColors.bind(this)
      this.colorSchemeMatcher.addEventListener('change', this.colorSchemeListener)
    }

    this.updateChartStyles()
  }

  /// Run when the element is disconnected from the DOM.
  ///
  /// This cleans up the chart and any listeners set up when connected.
  runDisconnected() {
    super.runDisconnected()

    if (this.colorSchemeMatcher) {
      this.colorSchemeMatcher.removeEventListener('change', this.colorSchemeListener)
      this.colorSchemeMatcher = null
      this.colorSchemeListener = null
    }
  }

  ////////// Configuration

  /// Creates and configures the chart element.
  setupChart() {
    this.canvasElement = document.createElement('canvas')
    this.appendChild(this.canvasElement)

    const options = {
      animation: false,
      interaction: {
        intersect: false
      },
      plugins: {
        legend: {
          position: 'bottom',
          align: 'start',
          labels: {
            usePointStyle: true
          }
        },
        tooltip: {
          mode: 'index',
          axis: 'xy',
          position: 'nearest',
          usePointStyle: true,
          callbacks: {
            title: (context) => this.formatKey(context[0].parsed.x, { full: true })
          }
        }
      }
    }

    switch (this.chartType) {
    case 'bar':
      options.datasets = {
        bar: {
          barPercentage: 0.9,
          categoryPercentage: 1
        }
      }
      options.scales = {
        x: {
          border: {
            display: false
          },
          grid: {
            display: false
          },
          ticks: {
            callback: (value, _index, _ticks) => this.formatKey(value)
          }
        },
        y: {
          border: {
            display: false
          },
          ticks: {
            maxTicksLimit: 5
          }
        }
      }

      if (this.chartSummary) {
        options.scales.x.display = false
      }

      break
    case 'line':
      options.scales = {
        x: {
          border: {
            display: false
          },
          grid: {
            display: false
          },
          ticks: {
            align: 'inner',
            maxRotation: 0,
            callback: (value, _index, _ticks) => { return this.formatKey(value) }
          }
        },
        y: {
          beginAtZero: true,
          border: {
            display: false
          },
          ticks: {
            maxTicksLimit: 5
          }
        }
      }

      if (this.chartSummary) {
        options.scales.x.display = false
      }

      break
    case 'pie':
    case 'doughnut':
      options.datasets = {}
      options.datasets[this.chartType] = { borderWidth: 0 }
      break
    case 'scatter':
      options.scales = {
        x: {
          border: {
            display: false
          },
          grid: {
            display: false
          }
        },
        y: {
          border: {
            display: false
          },
          ticks: {
            maxTicksLimit: 5
          }
        }
      }

      break
    }

    if (this.hideLegend) {
      options.plugins.legend = false
    }

    this.chart = new Chart(this.canvasElement, { type: this.chartType, data: this.chartData, options })
  }

  /// Updates the chart to display the given data.
  displayChartData(data) {
    this.chartData = data
    this.chart.data = data
    this.updateChartColors()
  }

  /// Updates the chart's styles.
  ///
  /// This retrieves fonts and other styles from the element's styles.
  updateChartStyles() {
    const computedStyle = this.getComputedStyle()

    const aspectRatio = computedStyle.aspectRatio
    if (aspectRatio.match(/^\d+\s*\/\s*\d+/)) {
      const parts = aspectRatio.split(/\s*\/\s*/)
      const numerator = parseFloat(parts[0])
      const denominator = parseFloat(parts[1])

      this.chart.options.aspectRatio = numerator / denominator
    }

    const primaryFont = {
      family: computedStyle.getPropertyValue('--font-family-primary')
    }

    const headingFont = {
      family: computedStyle.getPropertyValue('--font-family-heading'),
      weight: computedStyle.getPropertyValue('--font-weight-semibold')
    }

    if (this.chart.options.plugins.legend) {
      this.chart.options.plugins.legend.labels.font = headingFont
    }

    this.chart.options.plugins.tooltip.titleFont = headingFont
    this.chart.options.plugins.tooltip.labelFont = primaryFont

    switch (this.chartType) {
    case 'line':
    case 'scatter':
      this.chart.options.scales.x.ticks.font = primaryFont
      this.chart.options.scales.y.ticks.font = primaryFont
      break
    }

    this.updateChartColors()
  }

  /// Updates the chart's colors.
  ///
  /// This retrieves colors from the element's styles, which account for light or dark mode.
  updateChartColors() {
    const computedStyle = this.getComputedStyle()

    let colorNames = computedStyle.getPropertyValue('--chart-color-names')
    colorNames = colorNames.split(/,\s+/)

    const textColor = computedStyle.getPropertyValue('--color-text')
    const borderColor = computedStyle.getPropertyValue('--color-border')
    const bgHoverColor = computedStyle.getPropertyValue('--color-bg-hover')

    this.chart.options.color = textColor

    this.chart.options.plugins.tooltip.backgroundColor = bgHoverColor
    this.chart.options.plugins.tooltip.titleColor = textColor
    this.chart.options.plugins.tooltip.bodyColor = textColor

    switch (this.chartType) {
    case 'bar':
    case 'line':
    case 'scatter':
      this.chart.data.datasets.forEach((dataset, index) => {
        const colorIndex = index % colorNames.length
        const colorName = colorNames[colorIndex]

        const color = computedStyle.getPropertyValue(`--color-${colorName}`)

        dataset.borderColor = color
        dataset.backgroundColor = color
      })

      this.chart.options.scales.x.ticks.color = textColor
      this.chart.options.scales.y.ticks.color = textColor
      this.chart.options.scales.x.grid.color = borderColor
      this.chart.options.scales.y.grid.color = borderColor
      break
    case 'pie':
    case 'doughnut': {
      const colors = []
      const hoverColors = []

      this.chart.data.labels.forEach((_label, index) => {
        const colorIndex = index % colorNames.length
        const colorName = colorNames[colorIndex]

        colors.push(computedStyle.getPropertyValue(`--color-${colorName}`))
        hoverColors.push(computedStyle.getPropertyValue(`--color-${colorName}-hover`))
      })

      this.chart.data.datasets.forEach((dataset) => {
        dataset.backgroundColor = colors
        dataset.hoverBackgroundColor = hoverColors
      })

      break
    }
    }

    this.chart.update()
  }

  ////////// Formatting

  /// Outputs a key in a formatted state.
  ///
  /// This utilises the given format callback if available.
  formatKey(key, { full = false } = {}) {
    if (typeof this.formatKeyCallback === 'function') { return this.formatKeyCallback(key, { full }) }

    if (typeof key === 'number') {
      if (Array.isArray(this.chartData.labels)) {
        return this.chartData.labels[key]
      }
    }

    return key
  }
}

window.customElements.define('onspace-chart', OnspaceChart)
